import React from "react";
import { assetDisplay, useInstruments } from "@/lib/instruments";

/**
 * Crypto logo for a trading symbol. Accepts "BTCUSDT", "BTC", etc. — the quote
 * suffix is stripped to get the base asset. Falls back to a clean lettered
 * circle if the logo fails to load, so an unknown/altcoin ticker still renders
 * intentionally rather than a broken image. 2026-08 (Nick: logos in Live Prices).
 */
const CoinIcon: React.FC<{ symbol: string; size?: number; className?: string }> = ({
  symbol,
  size = 24,
  className = "",
}) => {
  /*
   * The asset through the name adjuster (2026-10-31): SNDKUSDTM, XYZSNDK and
   * SNDK_USDT are all SNDK, and a bare "USDC" stays USDC (never stripped to
   * nothing, which once left USDC without its logo).
   */
  const { data: reg } = useInstruments();
  const raw = (symbol || "").toUpperCase();
  const base = (assetDisplay(raw, reg) || raw).toUpperCase();
  const [failed, setFailed] = React.useState(false);

  // Reset when the symbol changes (list rows can be reused by key).
  React.useEffect(() => setFailed(false), [base]);

  if (!base) return null;

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center rounded-full bg-muted text-muted-foreground font-semibold shrink-0 ${className}`}
        style={{ width: size, height: size, fontSize: Math.max(9, size * 0.36) }}
        aria-label={base}
      >
        {base.slice(0, 3)}
      </div>
    );
  }

  return (
    <img
      src={`https://assets.coincap.io/assets/icons/${base.toLowerCase()}@2x.png`}
      alt={base}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`rounded-full shrink-0 ${className}`}
      style={{ width: size, height: size }}
    />
  );
};

export default CoinIcon;
